import React from 'react';
import { MapPin, Image as ImageIcon, Film, Hash, User, ChevronDown, X, SlidersHorizontal, Clock, TrendingUp } from 'lucide-react';
import { MAJOR_CITIES, budgetCountries } from '../../data/budget-countries';
import { MEDIA_TAGS } from '../../data/media-tags';
import { useSearchParams } from 'react-router-dom';
import * as amplitude from '@amplitude/analytics-browser';

interface MediaSearchProps {
  onSearch: (filters: {
    country?: string;
    mediaType?: 'image' | 'video' | 'all';
    sortBy: 'date' | 'popularity';
    tags: string[];
    userId?: string;
  }) => void;
  currentUserId: string;
}

export function MediaSearch({ onSearch, currentUserId }: MediaSearchProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [country, setCountry] = React.useState('');
  const [mediaType, setMediaType] = React.useState<'image' | 'video' | 'all'>('all');
  const [sortBy, setSortBy] = React.useState<'date' | 'popularity'>('date');
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const [showTagSelector, setShowTagSelector] = React.useState(false);
  const [showUserPosts, setShowUserPosts] = React.useState(false);
  const [showMobileFilters, setShowMobileFilters] = React.useState(false);
  const filterRef = React.useRef<HTMLDivElement>(null);

  const [tempFilters, setTempFilters] = React.useState({
    country,
    mediaType,
    sortBy,
    selectedTags,
    showUserPosts
  });

  React.useEffect(() => {
    const tag = searchParams.get('tag');
    const user = searchParams.get('user');
    if (tag && MEDIA_TAGS[tag]) {
      setSelectedTags([tag]);
      setTempFilters(prev => ({ ...prev, selectedTags: [tag] }));
    }
    if (user === 'me') {
      setShowUserPosts(true);
      setTempFilters(prev => ({ ...prev, showUserPosts: true }));
    }
  }, [searchParams]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowTagSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (showMobileFilters) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showMobileFilters]);

  const tagsByCategory = React.useMemo(() => {
    return Object.entries(MEDIA_TAGS).reduce((acc, [id, tag]) => {
      if (!acc[tag.category]) {
        acc[tag.category] = [];
      }
      acc[tag.category].push({ id, ...tag });
      return acc;
    }, {} as Record<string, Array<{ id: string; icon: string; label: string; category: string }>>);
  }, []);

  const categoryLabels: Record<string, string> = {
    status: 'Etapes PVT',
    logement: 'Type de logement',
    travail: 'Type de travail',
    lifestyle: 'Style de vie',
    social: 'Social'
  };

  const handleTagSelect = (tagId: string) => {
    if (showMobileFilters) {
      setTempFilters(prev => ({
        ...prev,
        selectedTags: prev.selectedTags.includes(tagId)
          ? prev.selectedTags.filter(t => t !== tagId)
          : [tagId]
      }));
    } else {
      setSelectedTags(prev => {
        const newTags = prev.includes(tagId) ? prev.filter(t => t !== tagId) : [tagId];
        return newTags;
      });
      setShowTagSelector(false);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (selectedTags.length > 0) params.set('tag', selectedTags[0]);
    if (showUserPosts) params.set('user', 'me');
    setSearchParams(params);

    onSearch({
      country: country || undefined,
      mediaType: mediaType === 'all' ? undefined : mediaType,
      sortBy,
      tags: selectedTags,
      userId: showUserPosts ? currentUserId : undefined
    });
  };

  const handleApplyFilters = () => {
    setCountry(tempFilters.country);
    setMediaType(tempFilters.mediaType);
    setSortBy(tempFilters.sortBy);
    setSelectedTags(tempFilters.selectedTags);
    setShowUserPosts(tempFilters.showUserPosts);

    const params = new URLSearchParams();
    if (tempFilters.selectedTags.length > 0) params.set('tag', tempFilters.selectedTags[0]);
    if (tempFilters.showUserPosts) params.set('user', 'me');
    setSearchParams(params);

    onSearch({
      country: tempFilters.country || undefined,
      mediaType: tempFilters.mediaType === 'all' ? undefined : tempFilters.mediaType,
      sortBy: tempFilters.sortBy,
      tags: tempFilters.selectedTags,
      userId: tempFilters.showUserPosts ? currentUserId : undefined
    });

    amplitude.track("Photos Filters");
    setShowMobileFilters(false);
  };

  const handleResetFilters = () => {
    amplitude.track("Photos Filters Reset");
    setTempFilters({
      country: '',
      mediaType: 'all',
      sortBy: 'date',
      selectedTags: [],
      showUserPosts: false
    });
  };

  React.useEffect(() => {
    if (!showMobileFilters) {
      const timer = setTimeout(() => { handleSearch(); }, 300);
      return () => clearTimeout(timer);
    }
  }, [country, mediaType, sortBy, selectedTags, showUserPosts]);

  const activeFilterCount = [
    country,
    mediaType !== 'all',
    selectedTags.length > 0,
    showUserPosts
  ].filter(Boolean).length;

  return (
    <>
      <div className="sm:hidden">
        <button
          onClick={() => {
            setTempFilters({ country, mediaType, sortBy, selectedTags, showUserPosts });
            setShowMobileFilters(true);
          }}
          className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm"
        >
          <div className="flex items-center gap-2.5">
            <SlidersHorizontal className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {activeFilterCount > 0
                ? `${activeFilterCount} filtre${activeFilterCount > 1 ? 's' : ''} actif${activeFilterCount > 1 ? 's' : ''}`
                : 'Filtrer les posts'}
            </span>
          </div>
          {activeFilterCount > 0 && (
            <span className="w-5 h-5 flex items-center justify-center bg-primary-500 text-white text-xs font-bold rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div className="hidden sm:block bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm overflow-hidden">
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-gray-100 dark:bg-gray-700/80 rounded-lg p-0.5">
              <button
                onClick={() => {
                  setSortBy('date');
                  onSearch({
                    country: country || undefined,
                    mediaType: mediaType === 'all' ? undefined : mediaType,
                    sortBy: 'date',
                    tags: selectedTags,
                    userId: showUserPosts ? currentUserId : undefined
                  });
                }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  sortBy === 'date'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                <span>Recents</span>
              </button>
              <button
                onClick={() => {
                  setSortBy('popularity');
                  onSearch({
                    country: country || undefined,
                    mediaType: mediaType === 'all' ? undefined : mediaType,
                    sortBy: 'popularity',
                    tags: selectedTags,
                    userId: showUserPosts ? currentUserId : undefined
                  });
                }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  sortBy === 'popularity'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <TrendingUp className="w-3.5 h-3.5" />
                <span>Populaires</span>
              </button>
            </div>

            <div className="flex items-center gap-1.5 ml-auto">
              <button
                onClick={() => {
                  setShowUserPosts(!showUserPosts);
                  if (!showUserPosts) {
                    setSearchParams(new URLSearchParams({ user: 'me' }));
                  } else {
                    const params = new URLSearchParams(searchParams);
                    params.delete('user');
                    setSearchParams(params);
                  }
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  showUserPosts
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <User className="w-4 h-4" />
                <span className="hidden lg:inline">Mes posts</span>
              </button>

              <div className="flex items-center bg-gray-100 dark:bg-gray-700/80 rounded-lg p-0.5">
                {[
                  { key: 'all' as const, label: 'Tout', icon: null },
                  { key: 'image' as const, label: 'Photos', icon: ImageIcon },
                  { key: 'video' as const, label: 'Videos', icon: Film }
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={() => setMediaType(item.key)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                      mediaType === item.key
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {item.icon && <item.icon className="w-3.5 h-3.5" />}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="relative flex-1 sm:flex-initial sm:w-48">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:text-white text-sm font-medium appearance-none transition-all"
              >
                <option value="">Tous les pays</option>
                {budgetCountries.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                ))}
              </select>
            </div>

            <div className="relative flex-1" ref={filterRef}>
              <button
                onClick={() => setShowTagSelector(!showTagSelector)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/60 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 transition-all"
              >
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-gray-400" />
                  <span>Filtrer par theme</span>
                  {selectedTags.length > 0 && (
                    <span className="w-5 h-5 flex items-center justify-center bg-primary-500 text-white rounded-full text-xs font-bold">
                      {selectedTags.length}
                    </span>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showTagSelector ? 'rotate-180' : ''}`} />
              </button>

              {showTagSelector && (
                <div className="absolute z-20 left-0 right-0 mt-1.5 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-3 max-h-[60vh] overflow-y-auto">
                    {Object.entries(tagsByCategory).map(([category, tags]) => (
                      <div key={category} className="mb-4 last:mb-0">
                        <h4 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">
                          {categoryLabels[category] || category}
                        </h4>
                        <div className="grid grid-cols-2 gap-1.5">
                          {tags.map(tag => (
                            <button
                              key={tag.id}
                              onClick={() => handleTagSelect(tag.id)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                selectedTags.includes(tag.id)
                                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/80 text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              <span className="text-base">{tag.icon}</span>
                              <span className="truncate">{tag.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {selectedTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {selectedTags.map(tagId => {
                const tag = MEDIA_TAGS[tagId];
                return (
                  <button
                    key={tagId}
                    onClick={() => handleTagSelect(tagId)}
                    className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium transition-all hover:opacity-80"
                  >
                    <span>{tag.icon}</span>
                    <span>{tag.label}</span>
                    <X className="w-3.5 h-3.5 ml-0.5 opacity-60" />
                  </button>
                );
              })}
              <button
                onClick={() => {
                  amplitude.track("Photos Filters Reset");
                  setSelectedTags([]);
                  setSearchParams(new URLSearchParams());
                }}
                className="text-xs font-medium text-gray-400 hover:text-primary-500 px-2 py-1.5 transition-colors"
              >
                Effacer
              </button>
            </div>
          )}
        </div>
      </div>

      {showMobileFilters && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 sm:hidden">
          <div
            className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-2xl max-h-[90vh] flex flex-col"
            style={{ height: '90vh' }}
          >
            <div className="sticky top-0 flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Filtres</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Trier par</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTempFilters(prev => ({ ...prev, sortBy: 'date' }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                      tempFilters.sortBy === 'date'
                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    Recents
                  </button>
                  <button
                    onClick={() => setTempFilters(prev => ({ ...prev, sortBy: 'popularity' }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                      tempFilters.sortBy === 'popularity'
                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    Populaires
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Type de media</h4>
                <div className="grid grid-cols-3 gap-2">
                  {(['all', 'image', 'video'] as const).map(type => (
                    <button
                      key={type}
                      onClick={() => setTempFilters(prev => ({ ...prev, mediaType: type }))}
                      className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                        tempFilters.mediaType === type
                          ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {type === 'all' ? 'Tout' : type === 'image' ? 'Photos' : 'Videos'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Pays</h4>
                <select
                  value={tempFilters.country}
                  onChange={(e) => setTempFilters(prev => ({ ...prev, country: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  <option value="">Tous les pays</option>
                  {budgetCountries.map((c) => (
                    <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Themes</h4>
                <div className="space-y-4">
                  {Object.entries(tagsByCategory).map(([category, tags]) => (
                    <div key={category}>
                      <h5 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        {categoryLabels[category] || category}
                      </h5>
                      <div className="grid grid-cols-2 gap-1.5">
                        {tags.map(tag => (
                          <button
                            key={tag.id}
                            onClick={() => handleTagSelect(tag.id)}
                            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                              tempFilters.selectedTags.includes(tag.id)
                                ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            <span>{tag.icon}</span>
                            <span className="truncate">{tag.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 p-4 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-2">
              <button
                onClick={handleApplyFilters}
                className="w-full px-4 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-primary-500/25"
              >
                Appliquer les filtres
              </button>
              <button
                onClick={handleResetFilters}
                className="w-full px-4 py-2.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium rounded-xl transition-colors text-sm"
              >
                Reinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
